import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";

import {
  getPreauthSession,
  clearPreauthSession,
} from "../components/helpers/authFlowStorage";

const shellStyle = {
  maxWidth: "520px",
  margin: "0 auto",
  paddingTop: "2.5rem",
};

const footerStyle = {
  marginTop: "1rem",
  textAlign: "center",
};

const backLinkStyle = {
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  color: "var(--color-accent)",
  font: "inherit",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
};

const successMessageStyle = {
  margin: 0,
  textAlign: "left",
  color: "var(--color-text)",
  lineHeight: 1.5,
};

const ExcursionJoin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applySession } = useAuth();

  const initial = getPreauthSession();

  const [phoneNumber, setPhoneNumber] = useState(initial.phoneNumber || "");
  const [name, setName] = useState(initial.name || "");
  const [surname, setSurname] = useState(initial.surname || "");
  const [excursionId, setExcursionId] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [successDetail, setSuccessDetail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setPhoneNumber(digits);
  };

  const handleExcursionIdChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setExcursionId(digits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    const normalizedPhone = phoneNumber.replace(/\D/g, "");

    if (!normalizedPhone) {
      alert(t("excursion_join.errors.missing_phone"));
      return;
    }

    if (!excursionId) {
      alert(t("excursion_join.errors.missing_excursion_id"));
      return;
    }

    if (!name.trim() || !surname.trim()) {
      alert(t("excursion_join.errors.missing_name"));
      return;
    }

    const exId = parseInt(excursionId, 10);
    if (!Number.isFinite(exId) || exId <= 0) {
      alert(t("excursion_join.errors.invalid_excursion_id"));
      return;
    }

    try {
      setIsLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/excursion-join`,
        {
          phone_number: normalizedPhone,
          name: name.trim(),
          surname: surname.trim(),
          excursion_id: exId,
        }
      );

      const appId = res.data?.app_id || "";
      const detail =
        res.data?.detail ||
        res.data?.message ||
        t("excursion_join.success.default");

      const joinedRole = res.data?.role || "member";

      if (
        appId &&
        res.data?.subscription_status === "approved" &&
        initial.preauthToken
      ) {
        const loginRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/login-final`,
          {
            app_id: appId,
            preauth_token: initial.preauthToken,
          }
        );

        const accessToken = loginRes.data?.access_token;
        const subscriptionStatus = String(
          loginRes.data?.subscription_status || ""
        ).toLowerCase();

        if (!accessToken) {
          throw new Error("No access token returned");
        }

        await applySession({
          token: accessToken,
          role: joinedRole,
          activeAppId: appId,
          subscriptionStatus,
        });

        clearPreauthSession();

        if (subscriptionStatus === "pending") {
          navigate("/pending", { replace: true });
          return;
        }

        navigate("/my-summary", { replace: true });
        return;
      }

      setSuccessDetail(detail);
      setSubmitted(true);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        t("excursion_join.errors.submit_failed");

      if (
        msg === "Invalid or expired pre-auth token" ||
        msg === "Invalid pre-auth token type" ||
        msg === "Invalid pre-auth token subject" ||
        msg === "Phone mismatch"
      ) {
        clearPreauthSession();
        alert(t("excursion_join.errors.session_expired"));
        navigate("/login", { replace: true });
        return;
      }

      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    navigate("/excursion-gate", { replace: true });
  };

  if (submitted) {
    return (
      <StandardScreen background="default">
        <ScreenContent>
          <div style={shellStyle}>
            <Card title={t("excursion_join.success.title")}>
              <Stack gap="4">
                <p style={successMessageStyle}>✅ {successDetail}</p>

                <Button size="lg" fullWidth onClick={goBack}>
                  {t("excursion_join.success.continue")}
                </Button>
              </Stack>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen background="default">
      <ScreenContent>
        <div style={shellStyle}>
          <Card
            title={t("excursion_join.title")}
            subtitle={t("excursion_join.subtitle")}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap="4">
                <FormField label={t("excursion_join.phone_label")}>
                  <TextInput
                    type="tel"
                    placeholder={t("excursion_join.phone_placeholder")}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    disabled={isLoading}
                  />
                </FormField>

                <FormField label={t("excursion_join.name_label")}>
                  <TextInput
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("excursion_join.name_placeholder")}
                    disabled={isLoading}
                  />
                </FormField>

                <FormField label={t("excursion_join.surname_label")}>
                  <TextInput
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    placeholder={t("excursion_join.surname_placeholder")}
                    disabled={isLoading}
                  />
                </FormField>

                <FormField label={t("excursion_join.excursion_label")}>
                  <TextInput
                    type="text"
                    inputMode="numeric"
                    placeholder={t("excursion_join.excursion_placeholder")}
                    value={excursionId}
                    onChange={handleExcursionIdChange}
                    disabled={isLoading}
                  />
                </FormField>

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("excursion_join.submitting")
                    : t("excursion_join.submit")}
                </Button>
              </Stack>
            </form>
          </Card>

          <div style={footerStyle}>
            <button
              type="button"
              onClick={goBack}
              disabled={isLoading}
              style={backLinkStyle}
            >
              {t("excursion_join.back")}
            </button>
          </div>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ExcursionJoin;